import { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";

export default function HorizontalImageStrip({ images }) {
  const appContext = useContext(AppContext);
  const containerRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [index, setIndex] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [slideWidth, setSlideWidth] = useState(0);

  async function init() {
    setReady(false);
    if (!images || images.length < 3) return; // minimum requirement
    setReady(true);
  }

  useEffect(() => {
    init();
  }, [images]);

  // Calculate layout (slidesPerView & slideWidth) and keep index valid
  useEffect(() => {
    if (!ready) return;

    function updateLayout() {
      const container = containerRef.current;
      const containerWidth = container ? container.clientWidth : window.innerWidth;
      // Bootstrap md breakpoint ~768px — adjust if your project uses a different one
      const spv = window.innerWidth >= 768 ? 3 : 1;
      const slides = Math.min(images.length, spv); // don't set slidesPerView > images available
      const sw = Math.floor(containerWidth / slides);

      setSlidesPerView(slides);
      setSlideWidth(sw);

      // ensure index is valid for new slidesPerView
      const maxIndex = Math.max(0, images.length - slides);
      setIndex((prev) => (prev > maxIndex ? 0 : prev));
    }

    updateLayout();

    //hack
    setTimeout(() => {
      updateLayout();
    }, 1000)
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [ready, images.length]);

  // Auto slide (advance, wrapping at maxIndex)
  useEffect(() => {
    if (!ready || slideWidth === 0) return;

    const interval = setInterval(() => {
      setIndex((prev) => {
        const maxIndex = Math.max(0, images.length - slidesPerView);
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [ready, slideWidth, slidesPerView, images.length]);

  if (!ready || slideWidth === 0) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  const trackWidth = slideWidth * images.length; // in px
  const transform = `translateX(-${index * slideWidth}px)`;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "300px", // adjust as needed
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          height: "100%",
          width: trackWidth, // React numeric style interpreted as px
          transform,
          transition: "transform 1s ease-in-out",
        }}
      >
        {images.map((img, i) => (
          <div
            key={i}
            style={{
              flex: `0 0 ${slideWidth}px`,
              width: `${slideWidth}px`,
              height: "100%",
            }}
          >
            <img
              src={img}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
